import {
  type PointerEvent,
  type WheelEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { floorSpriteMarkerLayout } from 'utils/robotIconLayout'

import style from './LocalFloorMapStage.module.css'

export interface LocalFloorMapMarker {
  x: number
  y: number
  angleDeg: number
  iconUrl: string
  headingOffsetDeg?: number
  iconSizePx?: number
  transitionMs?: number
  /** Occupancy meters per cell. With spriteLengthM, height follows the grid instead of iconSizePx. */
  resolutionM?: number
  /** Ground length of the full map PNG. Autonomics sprites use 2.13 m. */
  spriteLengthM?: number
  /** Pose to the front edge of that canvas, meters. */
  originToFrontM?: number | null
}

export interface LocalFloorMapTrailPoint {
  x: number
  y: number
}

type Props = {
  src: string
  alt: string
  marker?: LocalFloorMapMarker | null
  trailPoints?: LocalFloorMapTrailPoint[]
  onError?: () => void
  /** Keep zoom/pan when src updates (useful for fast live occupancy frame refresh). */
  preserveViewOnSrcChange?: boolean
}

function computeMarkerScreenPx(
  x: number,
  y: number,
  natural: { w: number; h: number },
  container: { w: number; h: number }
): { left: number; top: number } | null {
  if (!natural.w || !natural.h || !container.w || !container.h) return null
  const containerRatio = container.w / container.h
  const imageRatio = natural.w / natural.h
  let dw: number
  let dh: number
  if (containerRatio > imageRatio) {
    dh = container.h
    dw = dh * imageRatio
  } else {
    dw = container.w
    dh = dw / imageRatio
  }
  const offsetX = (container.w - dw) / 2
  const offsetY = (container.h - dh) / 2
  const scaleX = dw / natural.w
  const scaleY = dh / natural.h
  return { left: offsetX + x * scaleX, top: offsetY + (natural.h - y) * scaleY }
}

function parseScreenPoint(point: string): { left: number; top: number } | null {
  const [leftRaw, topRaw] = point.split(',')
  const left = Number(leftRaw)
  const top = Number(topRaw)
  if (!Number.isFinite(left) || !Number.isFinite(top)) return null
  return { left, top }
}

function normalizeAngleDeg(angle: number): number {
  let a = angle % 360
  if (a > 180) a -= 360
  if (a <= -180) a += 360
  return a
}

function shortestAngleDeltaDeg(fromDeg: number, toDeg: number): number {
  return normalizeAngleDeg(toDeg - fromDeg)
}

function distanceSquared(a: { left: number; top: number }, b: { left: number; top: number }): number {
  const dx = a.left - b.left
  const dy = a.top - b.top
  return dx * dx + dy * dy
}

const LocalFloorMapStage = ({ src, alt, marker, trailPoints, onError, preserveViewOnSrcChange = false }: Props) => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [container, setContainer] = useState<{ w: number; h: number }>({ w: 0, h: 0 })
  const [natural, setNatural] = useState<{ w: number; h: number }>({ w: 0, h: 0 })
  const [scale, setScale] = useState(1)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [animatedMarker, setAnimatedMarker] = useState<{ left: number; top: number; rotateDeg: number } | null>(null)
  const [markerImageFailed, setMarkerImageFailed] = useState(false)
  const animatedMarkerRef = useRef<{ left: number; top: number; rotateDeg: number } | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const dragRef = useRef({ active: false, x: 0, y: 0, tx: 0, ty: 0 })

  useEffect(() => {
    if (!preserveViewOnSrcChange) {
      setScale(1)
      setTranslate({ x: 0, y: 0 })
    }
    // Keep marker/natural image size across frequent occupancy src refreshes
    // to avoid visible marker blinking between frames.
    setMarkerImageFailed(false)
    if (animationFrameRef.current != null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }, [src, preserveViewOnSrcChange])

  useEffect(() => {
    setMarkerImageFailed(false)
  }, [marker?.iconUrl])

  useEffect(() => {
    const node = wrapRef.current
    if (!node) return
    setContainer({ w: node.clientWidth, h: node.clientHeight })
    const observer = new ResizeObserver(entries => {
      const cr = entries[0]?.contentRect
      if (!cr) return
      setContainer({ w: cr.width, h: cr.height })
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const markerScreen = useMemo(() => {
    if (!marker || !natural.w || !natural.h || !container.w || !container.h) return null
    const pos = computeMarkerScreenPx(marker.x, marker.y, natural, container)
    if (!pos) return null
    return { ...pos, rotateDeg: 90 - marker.angleDeg + (marker.headingOffsetDeg ?? 0) }
  }, [marker?.x, marker?.y, marker?.angleDeg, marker?.headingOffsetDeg, natural.w, natural.h, container.w, container.h])

  const transitionMs = Math.max(0, Math.min(8000, Number(marker?.transitionMs ?? 250)))
  const transitionMsRef = useRef(transitionMs)
  transitionMsRef.current = transitionMs

  useEffect(() => {
    if (!markerScreen) {
      setAnimatedMarker(null)
      animatedMarkerRef.current = null
      if (animationFrameRef.current != null) cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
      return
    }
    const from = animatedMarkerRef.current ?? markerScreen
    const to = markerScreen
    if (animationFrameRef.current != null) cancelAnimationFrame(animationFrameRef.current)
    animationFrameRef.current = null
    if (transitionMsRef.current <= 0) {
      animatedMarkerRef.current = to
      setAnimatedMarker(to)
      return
    }
    const startTime = performance.now()
    const rotateDeltaDeg = shortestAngleDeltaDeg(from.rotateDeg, to.rotateDeg)
    const animate = (now: number) => {
      const t = Math.max(0, Math.min(1, (now - startTime) / transitionMsRef.current))
      const next = {
        left: from.left + (to.left - from.left) * t,
        top: from.top + (to.top - from.top) * t,
        rotateDeg: from.rotateDeg + rotateDeltaDeg * t,
      }
      animatedMarkerRef.current = next
      setAnimatedMarker(next)
      if (t < 1) animationFrameRef.current = requestAnimationFrame(animate)
    }
    animationFrameRef.current = requestAnimationFrame(animate)
    return () => {
      if (animationFrameRef.current != null) cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }, [markerScreen])

  const trailScreenPoints = useMemo(() => {
    if (!trailPoints?.length || !natural.w || !natural.h || !container.w || !container.h) return [] as string[]
    const mapped = trailPoints
      .map(p => computeMarkerScreenPx(p.x, p.y, natural, container))
      .filter((p): p is { left: number; top: number } => !!p)
      .map(p => `${p.left},${p.top}`)
    if (animatedMarker && mapped.length > 0) {
      const first = parseScreenPoint(mapped[0])
      const last = parseScreenPoint(mapped[mapped.length - 1])
      const robotPoint = { left: animatedMarker.left, top: animatedMarker.top }
      if (first && last) {
        const endIndex = distanceSquared(first, robotPoint) <= distanceSquared(last, robotPoint) ? 0 : mapped.length - 1
        mapped[endIndex] = `${animatedMarker.left},${animatedMarker.top}`
      }
    }
    return mapped
  }, [trailPoints, natural, container, animatedMarker])

  const containedCellPx = useMemo(() => {
    if (!natural.w || !natural.h || !container.w || !container.h) return 0
    const containerRatio = container.w / container.h
    const imageRatio = natural.w / natural.h
    const dh = containerRatio > imageRatio ? container.h : container.w / imageRatio
    return dh / natural.h
  }, [natural.w, natural.h, container.w, container.h])
  const spriteLayout = floorSpriteMarkerLayout({
    spriteLengthM: marker?.spriteLengthM,
    originToFrontM: marker?.originToFrontM,
    resolutionM: marker?.resolutionM,
    cellPx: containedCellPx,
    fallbackHeightPx: marker?.iconSizePx ?? 40,
  })
  const iconSize = spriteLayout.heightPx
  const anchorFromTop = spriteLayout.anchorFromTop
  const canPan = scale > 1.001
  const applyScale = useCallback((next: number) => {
    const clamped = Math.max(1, Math.min(4, next))
    setScale(clamped)
    if (clamped <= 1) setTranslate({ x: 0, y: 0 })
  }, [])

  const onWheel = useCallback(
    (e: WheelEvent<HTMLDivElement>) => {
      e.preventDefault()
      const next = scale + (e.deltaY < 0 ? 0.12 : -0.12)
      applyScale(next)
    },
    [scale, applyScale]
  )

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!canPan) return
      dragRef.current = { active: true, x: e.clientX, y: e.clientY, tx: translate.x, ty: translate.y }
      ;(e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId)
    },
    [canPan, translate.x, translate.y]
  )

  const onPointerMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current
    if (!d.active) return
    setTranslate({ x: d.tx + (e.clientX - d.x), y: d.ty + (e.clientY - d.y) })
  }, [])

  const onPointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
    dragRef.current.active = false
    try {
      ;(e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId)
    } catch {
      // no-op
    }
  }, [])

  return (
    <div
      ref={wrapRef}
      className={style.wrap}
      onWheel={onWheel}
      onWheelCapture={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ cursor: canPan ? (dragRef.current.active ? 'grabbing' : 'grab') : 'default' }}
    >
      <div className={style.scene} style={{ transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})` }}>
        <img
          className={style.img}
          src={src}
          alt={alt}
          draggable={false}
          onLoad={e => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          onError={() => onError?.()}
        />
        {trailScreenPoints.length > 1 ? (
          <svg className={style.trailSvg} aria-hidden="true" focusable="false">
            <polyline
              className={style.trailLine}
              points={trailScreenPoints.join(' ')}
              style={{ strokeWidth: `${Math.max(1, (iconSize / 3) * 0.7)}px` }}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        ) : null}
        {marker && animatedMarker ? (
          <>
            <img
              className={style.robotIcon}
              src={marker.iconUrl}
              alt=""
              draggable={false}
              onError={() => {
                setMarkerImageFailed(true)
              }}
              style={{
                left: `${animatedMarker.left}px`,
                top: `${animatedMarker.top}px`,
                height: `${iconSize}px`,
                width: 'auto',
                opacity: markerImageFailed ? 0 : 1,
                transform: `translate(-50%, ${-anchorFromTop * 100}%) rotate(${animatedMarker.rotateDeg}deg)`,
                transformOrigin: `50% ${anchorFromTop * 100}%`,
              }}
            />
            {markerImageFailed ? (
              <div
                className={style.robotFallbackDot}
                style={{
                  left: `${animatedMarker.left}px`,
                  top: `${animatedMarker.top}px`,
                }}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  )
}

export default LocalFloorMapStage
